import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, CheckCircle, AlertCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Button, Input } from '../../components/ui';
import { useToast } from '../../components/ui/Toast';
import { checkPasswordStrength } from '../../utils/helpers';

const ResetPasswordPage = () => {
  const { updatePassword } = useAuth();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [searchParams] = useSearchParams();
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const passwordStrength = checkPasswordStrength(password);

  // Check for access token in URL (Supabase adds this)
  useEffect(() => {
    // Supabase handles the token automatically via auth state
    // Just need to make sure user is on this page from email link
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsLoading(true);

    try {
      await updatePassword(password);
      setSuccess(true);
      showToast('Password updated successfully!', 'success');
      setTimeout(() => {
        navigate('/signin');
      }, 2000);
    } catch (err) {
      setError(err.message || 'Failed to reset password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const strengthColors = {
    weak: 'bg-red-500',
    medium: 'bg-amber-500',
    strong: 'bg-green-500',
  };

  return (
    <>
      <Helmet>
        <title>Set New Password - CalcBuildAI</title>
        <meta name="description" content="Set a new password for your account." />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center py-12 px-4">
        <div className="max-w-md w-full">
          {/* Logo */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">CB</span>
              </div>
              <span className="font-bold text-2xl text-gray-900">Calc<span className="text-blue-600">Build</span>AI</span>
            </Link>
          </div>

          {/* Card */}
          <div className="bg-white rounded-2xl shadow-xl p-8">
            {success ? (
              // Success state
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">
                  Password Updated!
                </h1>
                <p className="text-gray-600 mb-6">
                  Your password has been successfully reset. Redirecting to sign in...
                </p>
              </div>
            ) : (
              // Form state
              <>
                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Lock className="w-8 h-8 text-blue-600" />
                  </div>
                  <h1 className="text-2xl font-bold text-gray-900 mb-2">
                    Set New Password
                  </h1>
                  <p className="text-gray-600">
                    Choose a strong password for your account
                  </p>
                </div>

                {error && (
                  <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-6 text-sm flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <span>{error}</span>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <Input
                      type="password"
                      label="New Password"
                      name="password"
                      placeholder="Enter new password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      autoComplete="new-password"
                    />
                    
                    {/* Password strength indicator */}
                    {password.length > 0 && (
                      <div className="mt-2">
                        <div className="flex gap-1 mb-1">
                          <div className={`h-1 flex-1 rounded ${password.length > 0 ? strengthColors[passwordStrength] : 'bg-gray-200'}`} />
                          <div className={`h-1 flex-1 rounded ${passwordStrength !== 'weak' ? strengthColors[passwordStrength] : 'bg-gray-200'}`} />
                          <div className={`h-1 flex-1 rounded ${passwordStrength === 'strong' ? strengthColors[passwordStrength] : 'bg-gray-200'}`} />
                        </div>
                        <p className="text-xs text-gray-500 capitalize">
                          Password strength: {passwordStrength}
                        </p>
                      </div>
                    )}
                  </div>

                  <Input
                    type="password"
                    label="Confirm Password"
                    name="confirmPassword"
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                    error={confirmPassword && password !== confirmPassword ? 'Passwords do not match' : ''}
                  />

                  <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    fullWidth
                    loading={isLoading}
                  >
                    Reset Password
                  </Button>
                </form>

                <div className="mt-6 text-center">
                  <Link to="/signin" className="text-gray-600 hover:text-gray-900 text-sm">
                    ← Back to Sign In
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ResetPasswordPage;
